import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import axios from "../api/api";
import { useAuth } from "../components/AuthContext";
import "../styles/CalendarPage.css";
import LeaveRequestAdmin from "./LeaveRequestAdmin";
import ApprovedLeavesPage from "./ApprovedLeavesPage";
import { Ban } from "lucide-react";

const leaveColors = {
  Sick: "#dc2626",
  Annual: "#ca8a04",
  Authorized: "#8e1cb1",
  Maternity: "#1e3a8a",
  Paternity: "#065f46",
  Bereavement: "#08b84bff",
  BankHoliday: "#1f2937",
};

// Helper function to format dates as DD-MM-YYYY
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const CalendarPage = () => {
  const { role } = useAuth();
  const [events, setEvents] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [activeTab, setActiveTab] = useState("calendar");

  const fetchLeaves = async () => {
    try {
      const approved = await axios.get("/leave/approved");
      // console.log("Approved leaves data:", approved.data);
      const approvedEvents = approved.data.map((leave) => ({
        title:
          role === "admin"
            ? `${leave.user?.name} (${leave.type})`
            : `${leave.user?.name}`,
        start: leave.start_date,
        end: new Date(new Date(leave.end_date).getTime() + 24 * 60 * 60 * 1000),
        allDay: true,
        color:
          role === "admin" ? leaveColors[leave.type] || "#8e1cb1" : "#3b82f6", // blue for employees
      }));

      const holidaysRes = await axios.get("/holidays");
      const holidays = holidaysRes.data.map((holiday) => ({
        title: holiday.description,
        start: holiday.date,
        allDay: true,
        color: "#1f2937",
      }));

      setEvents([...approvedEvents, ...holidays]);
    } catch (err) {
      console.error("Error fetching leaves/holidays:", err);
    }
  };

  const fetchPending = async () => {
    try {
      const res = await axios.get("/leave");
      const pending = res.data.filter((l) => l.status === "pending");
      setPendingLeaves(pending);
    } catch (err) {
      console.error("Error fetching pending leaves:", err);
    }
  };

  const handleDecision = async (id, action) => {
    try {
      await axios.post(`/leave/${action}/${id}`);
      await fetchLeaves();
      await fetchPending();
    } catch (err) {
      console.error(`Error trying to ${action} leave:`, err);
    }
  };

  useEffect(() => {
    fetchLeaves();
    if (role === "admin") {
      fetchPending();
    }
  }, [role]);

  return (
    <div className="calendar-container">
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === "calendar" ? "active" : ""}`}
          onClick={() => setActiveTab("calendar")}
        >
          Calendar
        </button>

        {role === "admin" && (
          <button
            className={`tab-btn ${activeTab === "requests" ? "active" : ""}`}
            onClick={() => setActiveTab("requests")}
          >
            Leave Requests ({pendingLeaves.length})
          </button>
        )}
        {role === "admin" && (
          <button
            className={`tab-btn ${activeTab === "apply" ? "active" : ""}`}
            onClick={() => setActiveTab("apply")}
          >
            Apply Leave
          </button>
        )}
        {role === "admin" && (
          <button
            className={`tab-btn ${activeTab === "allLeaves" ? "active" : ""}`}
            onClick={() => setActiveTab("allLeaves")}
          >
            All Leaves List
          </button>
        )}
      </div>

      {activeTab === "calendar" && (
        <>
          <div className="leave-legend">
            {Object.entries(leaveColors).map(([type, color]) => (
              <span
                key={type}
                className="leave-legend-item"
                style={{ backgroundColor: color, color: "#fff", padding: "2px 6px", borderRadius: "4px", marginRight: "8px" }}
              >
                {type}
              </span>
            ))}
          </div>
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            events={events}
            height="auto"
            weekends={true}
          />
        </>
      )}

      {activeTab === "requests" && role === "admin" && (
        <div className="requests-container">
          <h2 className="requests-title">Pending Leave Requests</h2>
          {pendingLeaves.length === 0 ? (
            <p className="no-requests">No pending requests</p>
          ) : (
            <div className="requests-list">
              {pendingLeaves.map((leave) => (
                <div key={leave.id} className="leave-item">
                  <div className="leave-details">
                    <p>
                      <strong>User:</strong>{" "}
                      {leave.user?.name || `ID: ${leave.user_id}`}
                    </p>
                    <p>
                      <strong>Email:</strong> {leave.user?.email}
                    </p>
                    <p>
                      <strong>Dates:</strong>{" "}
                      {formatDate(leave.start_date)} to{" "}
                      {formatDate(leave.end_date)}
                    </p>
                    <p>
                      <strong>Type:</strong>{" "}
                      <span
                        className="leave-type"
                        style={{ color: leaveColors[leave.type] || "#4b5563" }}
                      >
                        {leave.type}
                      </span>
                    </p>
                    <p>
                      <strong>Reason:</strong> {leave.reason}
                    </p>
                  </div>
                  <div className="leave-actions">
                    <button
                      className="approve-btn"
                      onClick={() => handleDecision(leave.id, "approve")}
                    >
                      Approve
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => handleDecision(leave.id, "reject")}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* // ) : (
        // <div className="requests-container">
        //   <h2 className="requests-title">Pending Leaves</h2>
        //   {pendingLeaves.length === 0 ? (
        //     <p className="no-requests">No current leave requests</p>
        //   ) : (
        //     <div className="requests-list">
        //       {pendingLeaves.map((leave) => (
        //         <div key={leave.id} className="leave-item employee-view">
        //           <div className="leave-details">
        //             <p>
        //               <strong>Name:</strong>{" "}
        //               {leave.user?.name || `ID: ${leave.user_id}`}
        //             </p>
        //           </div>
        //         </div>
        //       ))}
        //     </div>
        //   )}
        // </div> */}

      {activeTab === "apply" && (
        <LeaveRequestAdmin />
      )}
      {activeTab === "allLeaves" && (
        <ApprovedLeavesPage />
      )}
    </div>
  );
};

export default CalendarPage;
